import { CanActivateFn, ExtraOptions, RedirectCommand, Route, Router } from '@angular/router';
import { ForbiddenComponent } from './portal/redirection/forbidden/forbidden.component';
import { PageNotFoundComponent } from './portal/redirection/page-not-found/page-not-found.component';
import { SERVICES_UNAVAILABLE_PATH, ServicesUnavailableComponent } from './portal/redirection/services-unavailable/services-unavailable.component';
import { UnauthorizedComponent } from './portal/redirection/unauthorized/unauthorized.component';
import { AuthenticationService, authnGuard, AuthorizationService, AuthStorageService, FORBIDDEN, NOT_FOUND, UNAUTHORIZED } from '@sphere/platform';
import { DashboardComponent } from './portal/dashboard/dashboard.component';
import { spherePlatformApps } from './__app-registration';
import { LoginPageComponent } from './login/login-page.component';
import { SphereAppComponent } from './sphere-app.component';
import { inject } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';

export const SPHERE_HOME_PAGE_PATH = 'home';

// Create an initialization guard that ensures authorization is ready
const authorizationInitGuard = async () => {
    const authorization = inject(AuthorizationService);

    // If already loaded, continue
    if (authorization.loadedPrincipal()) {
        return true;
    }

    // Initialize if not already loading
    authorization.init();

    // Wait for the ready event
    try {
        await firstValueFrom(authorization.authorizationServiceReadyEvent);
        return true;
    } catch (error) {
        console.error('Failed to initialize authorization service', error);
        return false;
    }
};

const logoutGuard: CanActivateFn = async (route, state) => {
    const authentication = inject(AuthenticationService);
    const router = inject(Router);

    await authentication.logout({ logoutReason: 'manual logout', skipRedirect: true });

    const loginPath = router.parseUrl('/login');
    return new RedirectCommand(loginPath);
};

export const routes: Route[] = [
    {
        path: 'login',
        pathMatch: 'full',
        component: LoginPageComponent
    },
    {
        path: '',
        component: SphereAppComponent,
        canActivate: [authnGuard],
        canActivateChild: [authnGuard, authorizationInitGuard],
        data: { title: 'Home' },
        children: [
            {
                path: SPHERE_HOME_PAGE_PATH,
                pathMatch: 'full',
                data: { title: 'Home', noPadding: true },
                component: DashboardComponent
            },
            ...spherePlatformApps.sphereAppRoutes,
            {
                path: NOT_FOUND.path,
                component: PageNotFoundComponent,
                data: { title: 'Not found', noPadding: true }
            },
            {
                path: UNAUTHORIZED.path,
                component: UnauthorizedComponent,
                data: { title: 'Unauthorized' }
            },
            {
                path: FORBIDDEN.path,
                component: ForbiddenComponent,
                data: { title: 'Forbidden' }
            },
            {
                path: SERVICES_UNAVAILABLE_PATH,
                component: ServicesUnavailableComponent,
                data: { title: 'Services unavailable' }
            },
            {
                path: '',
                pathMatch: 'full',
                redirectTo: SPHERE_HOME_PAGE_PATH
            }
        ]
    },
    {
        path: 'logout',
        canActivate: [logoutGuard],
        component: LoginPageComponent
    },
    {
        path: '**',
        redirectTo: NOT_FOUND.path
    }
];

export const routingConfiguration: ExtraOptions = {
    scrollPositionRestoration: 'enabled',
    onSameUrlNavigation: 'reload',
    bindToComponentInputs: true
};
